<script setup lang="ts">
import { useAuthStore } from "@/stores/auth";
import Lucide from "@/components/Base/Lucide";
import DashboardLoans from "@/modules/Loans/components/DashboardLoans.vue";
import DashboardReproductions from "@/modules/Dashboard/components/DashboardReproductions.vue";
import DashboardPublications from "@/modules/Dashboard/components/DashboardPublications.vue";
import StatCard from "@/modules/Dashboard/components/StatCard.vue";
import DashboardUserLoans from "@/modules/Loans/components/DashboardUserLoans.vue";
import { ref, onMounted, computed } from "vue";
import api from "@/utils/axios";
import { usePolling } from "@template/composables/usePolling";
import { Tab } from "@/components/Base/Headless";
import { useI18n } from "vue-i18n";
import PageTitle from "@template/components/PageTitle/PageTitle.vue";

const { t } = useI18n();
const authStore = useAuthStore();
const isAdmin = computed(() => authStore.user?.role === 'ADMIN');
const isManagement = computed(() => ["ADMIN", "TECNICO"].includes(authStore.user?.role));
const stats = ref<any>(null);

const fetchStats = async () => {
    if (!isManagement.value) return; 
    try {
        const res = await api.get('/dashboard/stats');
        stats.value = res.data.data;
    } catch(e) {
        console.error("Stats Error", e);
    }
};

const loansRef = ref();
const docsRef = ref();
const reproRef = ref();
const pubRef = ref();
const activeTab = ref(0);

const handleTabChange = (index: number) => {
    activeTab.value = index;
    if (index === 0 && loansRef.value) {
        loansRef.value.refresh();
    } else if (index === 1 && docsRef.value) {
        docsRef.value.refresh();
    } else if (index === 2 && reproRef.value) {
        reproRef.value.refresh();
    } else if (index === 3 && pubRef.value) {
        pubRef.value.refresh();
    }
};

const { lastUpdate } = usePolling(async () => {
    if (!isManagement.value) return { totalPending: null };
    try {
        const res = await api.get('/system/check-updates');
        return {
            totalPending: res.data?.totalPending,
            title: 'Arqus',
            body: t('dashboard.new_requests_notification'),
            onUpdate: () => {
                fetchStats();
                if (loansRef.value) loansRef.value.refresh();
                if (docsRef.value) docsRef.value.refresh();
                if (reproRef.value) reproRef.value.refresh();
                if (pubRef.value) pubRef.value.refresh();
            }
        };
    } catch (e) { /* silent fail */ }
    return { totalPending: null };
});

onMounted(() => {
    if (isManagement.value) {
        fetchStats();
    }
});
</script>

<template>
  <div class="grid grid-cols-12 gap-6">
    <div class="col-span-12 2xl:col-span-12">
      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-12 mt-8">
          <div class="intro-y flex items-center">
                <PageTitle
                  :title="$t('dashboard.title')"
                  :subtitle="$t('dashboard.welcome_msg')"
                  icon="GaugeCircle"
                  variant="indigo"
                />
          </div>
          
          <!-- Stats Grid -->
          <div v-if="stats && isManagement" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mt-5" style="display: none;">
              <StatCard 
                :title="t('dashboard.stats.registered_users')" 
                :value="stats.users.total" 
                :subValue="t('dashboard.stats.new_last_year', { count: stats.users.new_last_year })"
                icon="Users"
                variant="primary"
              />
              <StatCard 
                :title="t('dashboard.stats.countries')" 
                :value="stats.users.distinct_countries" 
                :subValue="t('dashboard.stats.distinct_countries')"
                icon="Globe"
                variant="secondary"
              />
              <StatCard 
                :title="t('dashboard.stats.documents')" 
                :value="stats.documents.total" 
                icon="FileText"
                variant="info"
              />
              <StatCard 
                :title="t('dashboard.stats.loans')" 
                :value="stats.loans.total" 
                :subValue="t('dashboard.stats.last_year_count', { count: stats.loans.last_year })"
                icon="BookOpen"
                variant="warning"
              />
              <StatCard 
                :title="t('dashboard.stats.reproductions')" 
                :value="stats.reproductions.total" 
                :subValue="t('dashboard.stats.last_year_count', { count: stats.reproductions.last_year })"
                icon="Printer"
                variant="purple"
              />
              <StatCard 
                :title="t('dashboard.stats.publications')" 
                :value="stats.publications.total" 
                :subValue="t('dashboard.stats.last_year_count', { count: stats.publications.last_year })"
                icon="Book"
                variant="success"
              />
          </div>

          <div class="grid grid-cols-12 gap-6 mt-5" >
            <div class="col-span-12 intro-y" style="display: none;">
              <div class="box p-5 bg-primary text-white">
                <div class="flex items-center">
                    <div class="text-2xl font-bold text-slate-800">{{ $t('dashboard.welcome') }} {{ authStore.user?.first_name }}</div>
                </div>
                <div class="mt-2 text-sm text-slate-500">
                    {{ $t('dashboard.welcome_msg') }}
                </div>
              </div>
            </div>
            
            <div class="col-span-12 intro-y box p-5" v-if="isManagement">
                <Tab.Group @change="handleTabChange">
                    <Tab.List variant="boxed-tabs">
                        <Tab>
                            <Tab.Button
                                as="button"
                                :class="[
                                    'w-full py-2 text-sm flex items-center justify-center gap-2 font-medium',
                                    activeTab === 0
                                        ? 'text-white bg-emerald-600 border-emerald-600 shadow-sm'
                                        : 'text-emerald-700 bg-emerald-50 border-emerald-200',
                                ]"
                            >
                                <Lucide
                                    icon="BookKey"
                                    class="w-[24px] h-[24px] stroke-[1.3]"
                                    :class="activeTab === 0 ? 'text-white' : 'text-emerald-600'"
                                />
                                {{ $t('dashboard.daily_loans') }}
                            </Tab.Button>
                        </Tab>
                        <Tab>
                            <Tab.Button
                                as="button"
                                :class="[
                                    'w-full py-2 text-sm flex items-center justify-center gap-2 font-medium',
                                    activeTab === 1
                                        ? 'text-white bg-cyan-600 border-cyan-600 shadow-sm'
                                        : 'text-cyan-700 bg-cyan-50 border-cyan-200',
                                ]"
                            >
                                <Lucide
                                    icon="FileText"
                                    class="w-[24px] h-[24px] stroke-[1.3]"
                                    :class="activeTab === 1 ? 'text-white' : 'text-cyan-600'"
                                />
                                {{ $t('dashboard.daily_docs') }}
                            </Tab.Button>
                        </Tab>
                        <Tab v-if="isManagement">
                            <Tab.Button
                                as="button"
                                :class="[
                                    'w-full py-2 text-sm flex items-center justify-center gap-2 font-medium',
                                    activeTab === 2
                                        ? 'text-white bg-violet-600 border-violet-600 shadow-sm'
                                        : 'text-violet-700 bg-violet-50 border-violet-200',
                                ]"
                            >
                                <Lucide
                                    icon="Printer"
                                    class="w-[24px] h-[24px] stroke-[1.3]"
                                    :class="activeTab === 2 ? 'text-white' : 'text-violet-600'"
                                />
                                {{ $t('dashboard.pending_reproductions') }} ({{ stats?.reproductions?.open || 0 }})
                            </Tab.Button>
                        </Tab>
                         <Tab v-if="isManagement">
                            <Tab.Button
                                as="button"
                                :class="[
                                    'w-full py-2 text-sm flex items-center justify-center gap-2 font-medium',
                                    activeTab === 3
                                        ? 'text-white bg-amber-600 border-amber-600 shadow-sm'
                                        : 'text-amber-700 bg-amber-50 border-amber-200',
                                ]"
                            >
                                <Lucide
                                    icon="BookOpen"
                                    class="w-[24px] h-[24px] stroke-[1.3]"
                                    :class="activeTab === 3 ? 'text-white' : 'text-amber-600'"
                                />
                                {{ $t('dashboard.pending_publications') }} ({{ stats?.publications?.open || 0 }})
                            </Tab.Button>
                        </Tab>
                    </Tab.List>
                    <Tab.Panels class="mt-5">
                        <Tab.Panel class="leading-relaxed">
                             <DashboardLoans mode="loans" ref="loansRef" />
                        </Tab.Panel>
                        <Tab.Panel class="leading-relaxed">
                             <DashboardLoans mode="documents" ref="docsRef" />
                        </Tab.Panel>
                        <Tab.Panel v-if="isManagement" class="leading-relaxed">
                            <DashboardReproductions ref="reproRef" />
                        </Tab.Panel>
                        <Tab.Panel v-if="isManagement" class="leading-relaxed">
                            <DashboardPublications ref="pubRef" />
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>
            </div>
            
            <template v-else>
                <DashboardUserLoans />
            </template>
      </div>
    </div>
  </div>
</div>
</div>

  <div class="mt-8 pb-10 text-center text-sm text-slate-500 intro-y w-full">
    {{ $t('dashboard.last_sync', { date: lastUpdate || '---' }) }}
  </div>
</template>
